import { useState, useEffect, useCallback, useMemo } from 'react';
import { message, Button, Checkbox, Modal } from 'antd';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons';
import ItemTabs from './ItemTabs';
import ScoreEditor from '../../components/ScoreEditor';
import InputItem from '../../components/InputItem';
import RadioItem from '../../components/RadioItem';
import DataTable, { DragHandle } from '../../components/DataTable';
import Operator from '../../components/Operator';
import AddScoreGroup from '../../components/AddScoreGroup';

import request from '@/request';

const Counter = ({ onChange, round }) => {
  return (
    <>
      <button
        onClick={() => onChange(Math.max(1, round - 1))}
        className="inline-flex items-center justify-center w-6 h-6 text-slate-400 border border-slate-400 rounded-full hover:text-slate-400 hover:border-slate-400 transition-colors duration-200 cursor-pointer"
        title="减少"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 12H4"
          />
        </svg>
      </button>
      <span className="font-medium text-slate-900 w-4 text-center">
        {round}
      </span>
      <button
        onClick={() => onChange(round + 1)}
        className="inline-flex items-center justify-center w-6 h-6 text-blue-500 border border-blue-500 rounded-full hover:text-blue-600 hover:border-blue-600 transition-colors duration-200 cursor-pointer"
        title="增加"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
      </button>
    </>
  );
};

/** 管理赛项计分规则及计分项配置。 */
export default function ItemsContent({ items }) {
  const [activeItem, setActiveItem] = useState(items[0] || null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  /* savedFormData：记录上次保存或加载时的 formData 快照，用于脏检查 */
  const [savedFormData, setSavedFormData] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemsGroup, setItemsGroup] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [addTypeModalVisible, setAddTypeModalVisible] = useState(false);
  const [groupName, setGroupName] = useState('');

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (!activeItem?.id) return;
    request
      .get('/events/admin/score/item/list/' + activeItem.id)
      .then((res) => {
        const loaded = {
          baseScore: res?.rule?.baseScore || 0,
          round: res?.rule?.round || 0,
          roundTime: res?.rule?.roundTime || 0,
          restTime: res?.rule?.restTime || 0,
          scoreType: res?.rule?.scoreType || 2,
          rankType: res?.rule?.rankType || 2,
          roundAggregation: res?.rule?.roundAggregation || 1,
          tieRound: res?.rule?.tieRound || 1,
          tieRoundTime: res?.rule?.tieRoundTime || 0,
          overtimeTime: res?.rule?.overtimeTime || 0,
          penaltyTime: res?.rule?.penaltyTime || 0,
          tieBreakerType: res?.rule?.tieBreakerType || 1,
        };
        /* 同步设置表单数据与快照，保证初始状态 isDirty 为 false */
        setFormData(loaded);
        setSavedFormData(loaded);
        if (activeItem?.contestFormat === 2 && !(res?.groups || []).length) {
          setItemsGroup([{ name: '惩罚机制', items: [] }]);
        } else {
          setItemsGroup(res?.groups || []);
        }
      });
  }, [activeItem]);

  const submitData = useCallback(
    async (data) => {
      let newGroups = [...itemsGroup];
      if (data?.id) {
        newGroups = newGroups.map((g) => ({
          name: g.name,
          items: g.items.map((i) => (i.id === data.id ? data : i)),
        }));
      } else if (data?.name) {
        const groupIndex = newGroups.findIndex((g) => g.name === groupName);
        if (groupIndex === -1) {
          newGroups.push({ name: groupName, items: [data] });
        } else {
          newGroups[groupIndex].items.push(data);
        }
      }

      await request.post(`/events/admin/score/item/${activeItem.id}`, {
        rule: formData,
        groups: newGroups,
      });

      message.success('保存成功');
      setLoading(false);
      /* 保存成功后更新快照，重置脏状态 */
      setSavedFormData({ ...formData });

      /* 重新拉取后端最新数据，确保 groups.items 中每条记录携带后端分配的 id，
         防止新增记录因本地无 id 导致删除接口拼接出 undefined */
      const fresh = await request.get(
        '/events/admin/score/item/list/' + activeItem.id
      );
      setItemsGroup(fresh?.groups || []);
    },
    [activeItem, formData, itemsGroup, groupName]
  );

  const onSortSubmit = useCallback(
    async (data) => {
      const theIndex = itemsGroup.findIndex((g) => {
        return Boolean(g.items.find((i) => data.find((d) => d.id === i.id)));
      });
      try {
        await request.post(`/events/admin/score/item/${activeItem.id}`, {
          rule: formData,
          groups: itemsGroup.map((g, i) =>
            i === theIndex ? { name: g.name, items: data } : g
          ),
        });
        message.success('排序已更新');
      } catch {
        message.error('排序更新失败');
      }
    },
    [activeItem, formData, itemsGroup]
  );

  const onAddGroupType = (name, back = false) => {
    if (!name) {
      message.warning('请输入类型');
      return;
    }
    setItemsGroup((last) => {
      const newList = [...last];
      if (back) {
        if (!newList.find((item) => item.name === name + '（出发路线）')) {
          newList.push({ name: name + '（出发路线）', items: [] });
        }
        if (!newList.find((item) => item.name === name + '（返程路线）')) {
          newList.push({ name: name + '（返程路线）', items: [] });
        }
      } else if (newList.find((item) => item.name === name)) {
        message.warning('类型已存在');
        return newList;
      } else {
        newList.push({ name, items: [] });
      }
      return newList;
    });
    setAddTypeModalVisible(false);
  };

  const banAdd = useMemo(() => {
    const group = itemsGroup[0];
    if (group && group.name === '惩罚机制' && group.items.length === 2)
      return true;
  }, [itemsGroup]);

  const getColumns = (groupName) => {
    return [
      {
        title: '排序',
        key: 'sort',
        align: 'center',
        render: () => <DragHandle />,
        width: 60,
      },
      {
        title: '名称',
        dataIndex: 'name',
        key: 'name',
        width: 400,
      },
      {
        title: '类型',
        dataIndex: 'typeName',
        key: 'typeName',
        width: 200,
      },
      {
        title: '规则内容',
        key: 'content',
        render: (_, record) => {
          if (record.scoreTypeId === 1 || record.scoreTypeId === 8) {
            return <span>{record.options.map((o) => o.score).join(',')}</span>;
          } else if (record.scoreTypeId === 2) {
            return (
              <span>
                最低得分{record.attr.min},最高得分{record.attr.max}
              </span>
            );
          } else if (record.scoreTypeId === 3) {
            return (
              <span>
                每次得分{record.attr.per},最高次数{record.attr.max}
              </span>
            );
          } else if (record.scoreTypeId === 4) {
            return (
              <span>
                每次加时{record.attr.per}秒，最多加时{record.attr.max}次
                {/* ，犯规{record.attr.tolerate}次不扣分，犯规{record.attr.maxTolerate}次分数清零 */}
              </span>
            );
          } else if (record.scoreTypeId === 5) {
            return (
              <span>
                最低扣分{record.attr.min},最高扣分{record.attr.max}
              </span>
            );
          } else if (record.scoreTypeId === 10 || record.scoreTypeId === 11) {
            return (
              <span>
                每次惩罚，对手加{record.attr.them}分，我方减{record.attr.we}分
              </span>
            );
          }
          return '';
        },
      },
      {
        title: '操作',
        key: 'action',
        align: 'right',
        width: 100,
        render: (_, record) => (
          <Operator
            record={record}
            onEdit={() => {
              setSelectedItem({ ...record, groupName });
              setModalVisible(true);
            }}
            removeAPI={'/events/admin/score/item'}
            onRemove={() => {
              setItemsGroup((last) =>
                [...last].map((g) => ({
                  name: g.name,
                  items: g.items.filter((i) => i.id !== record.id),
                }))
              );
            }}
          />
        ),
      },
    ];
  };

  const changSort = useCallback(
    async (index, type) => {
      if (!activeItem?.id) return;
      let newGroups = [...itemsGroup];
      if (type === 1 && index > 0) {
        [newGroups[index], newGroups[index - 1]] = [
          newGroups[index - 1],
          newGroups[index],
        ];
      } else if (type === 2 && index < newGroups.length - 1) {
        [newGroups[index], newGroups[index + 1]] = [
          newGroups[index + 1],
          newGroups[index],
        ];
      }
      await request.post(`/events/admin/score/item/${activeItem?.id}`, {
        rule: formData,
        groups: newGroups,
      });
      setItemsGroup(newGroups);
    },
    [activeItem, formData, itemsGroup]
  );

  /**
   * handleTabChange：切换赛项 Tab 时的拦截函数
   * 若当前表单存在未保存变更（isDirty），弹窗让用户选择：
   *   - 保存并切换：调用保存接口，成功后切换；失败则 toast 提示并阻止切换
   *   - 放弃更改：直接切换，丢弃本地修改
   * @param {object} nextItem - 用户点击的目标赛项对象
   */
  const handleTabChange = useCallback(
    (nextItem) => {
      /* 同一 tab 直接忽略 */
      if (nextItem?.id === activeItem?.id) return;

      /* 比较当前 formData 与快照，判断是否有未保存变更 */
      const isDirty =
        JSON.stringify(formData) !== JSON.stringify(savedFormData);

      if (!isDirty) {
        setActiveItem(nextItem);
        return;
      }

      Modal.confirm({
        title: '配置内容已更改',
        content: '切换赛项前请选择如何处理当前未保存的更改。',
        okText: '保存并切换',
        cancelText: '放弃更改',
        onOk: async () => {
          try {
            /* 直接调用保存接口，不依赖 submitData 的 groups 合并逻辑，
               此处仅保存 rule 配置（平局机制等），groups 不变 */
            await request.post(`/events/admin/score/item/${activeItem.id}`, {
              rule: formData,
              groups: itemsGroup,
            });
            message.success('保存成功');
            setSavedFormData({ ...formData });
            setActiveItem(nextItem);
          } catch {
            message.error('保存失败，请重试');
            /* 抛出异常使 Modal 的 onOk Promise reject，阻止 Modal 关闭并阻止切换 */
            throw new Error('save failed');
          }
        },
        onCancel: () => {
          /* 放弃更改：直接切换，formData 会在 useEffect 中被新 Tab 数据覆盖 */
          setActiveItem(nextItem);
        },
      });
    },
    [activeItem, formData, savedFormData, itemsGroup]
  );

  return (
    <div className="space-y-6">
      <ItemTabs
        items={items}
        activeType={activeItem}
        onChange={handleTabChange}
      />
      {!activeItem?.id && (
        <div className="flex min-h-60 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
          请先配置赛项
        </div>
      )}
      {activeItem?.id && (
        <div className="flex flex-col gap-4">
          <div className="w-full items-center justify-between space-y-2 flex-col">
            <div className="w-full flex flex-col gap-2 items-start">
              <div className="w-full flex items-center justify-between">
                <h2 className="font-extrabold text-lg text-stone-800">
                  {activeItem?.contestFormat === 1 ? '成绩计算' : '比赛轮次'}
                </h2>
                <Button
                  type="primary"
                  loading={loading}
                  onClick={() => submitData()}
                  className="float-right"
                >
                  <CheckOutlined />
                  <span>保存</span>
                </Button>
              </div>
              {/* contestFormat===2 时换为多行布局，否则保持单行 */}
              <div className={`flex gap-4 ${activeItem?.contestFormat === 2 ? 'flex-col items-start' : 'items-center'}`}>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-700">
                    {activeItem?.contestFormat === 1 ? '跑酷机会' : '场次数量'}
                  </span>
                  <Counter
                    /*
                     * contestFormat===2 时场次数量锁死为 2，小程序功能尚未完成；
                     * 点击加减号拦截操作并提示用户，不允许修改
                     */
                    onChange={
                      activeItem?.contestFormat === 2
                        ? () => message.warning('暂时不能修改当前配置')
                        : (value) => updateField('round', value)
                    }
                    round={formData.round}
                  />
                  {/* contestFormat===2 时在 Counter 右侧展示提示文案 */}
                  {activeItem?.contestFormat === 2 && (
                    <span className="text-sm text-slate-400">（数量2为传统上下半场）</span>
                  )}
                </div>
                {/* 竞速赛展示成绩计算，对抗赛保留时间配置。 */}
                {activeItem?.contestFormat === 1 ? (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-700">成绩计算：</span>
                    <RadioItem
                      value={formData.roundAggregation}
                      onChange={(value) =>
                        updateField('roundAggregation', value)
                      }
                      options={[
                        { value: 1, label: '合并求和' },
                        { value: 2, label: '单次最优' },
                      ]}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-700">每场时长</span>
                    <InputItem
                      type="number"
                      value={formData.roundTime}
                      onChange={(value) => updateField('roundTime', value)}
                      className="w-20"
                      suffix="秒"
                    />
                    <span className="text-sm text-slate-700">中场休息</span>
                    <InputItem
                      type="number"
                      value={formData.restTime}
                      onChange={(value) => updateField('restTime', value)}
                      className="w-20"
                      suffix="秒"
                    />
                  </div>
                )}
              </div>
              {/* <Checkbox
                checked={!!formData.ifTimeScore}
                onChange={(e) =>
                  updateField('ifTimeScore', e.target.checked ? 1 : 0)
                }
              >
                计时是否算分
              </Checkbox> */}
            </div>
            {activeItem?.contestFormat === 1 && (
              <>
                <div className="flex flex-col gap-2 items-start">
                  <h2 className="text-stone-800 font-semibold text-lg">
                    记分项设置
                  </h2>
                  <Button
                    color="cyan"
                    variant="solid"
                    onClick={() => {
                      setSelectedItem(null);
                      setAddTypeModalVisible(true);
                    }}
                  >
                    <PlusOutlined />
                    <span>添加计分类型</span>
                  </Button>
                </div>
              </>
            )}
            {activeItem?.contestFormat === 2 && (
              <div className="flex flex-col gap-2 items-start">
                <h2 className="text-stone-800 font-semibold text-lg">
                  平局机制
                </h2>
                {/* 平局机制类型选择：点球(1)、加时赛(2)、闪电战(3) */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => {
                      updateField('tieBreakerType', 1);
                      updateField('overtimeTime', 0);
                      updateField('penaltyTime', 0);
                    }}
                    type={formData.tieBreakerType === 1 ? 'primary' : ''}
                  >
                    点球
                  </Button>
                  <Button
                    onClick={() => {
                      updateField('tieBreakerType', 2);
                      updateField('tieRound', 1);
                      updateField('tieRoundTime', 0);
                    }}
                    type={formData.tieBreakerType === 2 ? 'primary' : ''}
                  >
                    加时赛
                  </Button>
                  <Button
                    onClick={() => {
                      updateField('tieBreakerType', 3);
                      updateField('tieRound', 1);
                      updateField('tieRoundTime', 0);
                      updateField('penaltyTime', 0);
                    }}
                    type={formData.tieBreakerType === 3 ? 'primary' : ''}
                  >
                    闪电战
                  </Button>
                </div>
                {/* 点球：点球轮次 + 每轮时长 */}
                {formData.tieBreakerType === 1 && (
                  <>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-700">点球轮次</span>
                      <Counter
                        onChange={(value) => updateField('tieRound', value)}
                        round={formData.tieRound}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-700">每轮时长</span>
                      <InputItem
                        type="number"
                        value={formData.tieRoundTime}
                        onChange={(value) => updateField('tieRoundTime', value)}
                        className="w-30"
                        suffix="秒"
                      />
                      <span className="text-sm text-slate-700">
                        （0代表不限时）
                      </span>
                    </div>
                  </>
                )}
                {/* 加时赛：加时赛时间 + tip */}
                {formData.tieBreakerType === 2 && (
                  <>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-700">加时赛时间</span>
                      <InputItem
                        type="number"
                        value={formData.overtimeTime}
                        onChange={(value) => updateField('overtimeTime', value)}
                        className="w-30"
                        suffix="秒"
                      />
                      <span className="text-sm text-slate-700">
                        （0代表不限时）
                      </span>
                    </div>
                    <span className="text-sm text-slate-400">*若加时平局则进入点球</span>
                  </>
                )}
                {/* 闪电战：无需时间配置 */}
              </div>
            )}
          </div>
          {itemsGroup.map((group, index) => (
            <div
              className="flex flex-col items-start border border-slate-200 rounded-lg overflow-hidden p-4"
              key={group.name}
            >
              <div className="w-full flex items-center justify-between mb-4">
                <div className="flex gap-4">
                  <h2 className="text-stone-800 font-semibold text-lg">
                    {group.name}
                  </h2>
                  {index > 0 && (
                    <Button
                      className="text-sm"
                      onClick={() => changSort(index, 1)}
                    >
                      上移
                    </Button>
                  )}
                  {itemsGroup.length !== index + 1 && (
                    <Button
                      className="text-sm"
                      onClick={() => changSort(index, 2)}
                    >
                      下移
                    </Button>
                  )}
                </div>
                <Button
                  onClick={() => {
                    setGroupName(group.name);
                    setSelectedItem(null);
                    setModalVisible(true);
                  }}
                  disabled={banAdd}
                >
                  <PlusOutlined />
                  <span className="text-sm text-slate-700">添加</span>
                </Button>
              </div>
              <DataTable
                columns={getColumns(group.name)}
                list={group.items}
                onSortSubmit={onSortSubmit}
              />
            </div>
          ))}
        </div>
      )}
      <ScoreEditor
        key={modalVisible ? 'editorShow' : 'editorHidden'}
        contestType={activeItem?.contestFormat}
        item={selectedItem}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        groupName={groupName}
        onSubmit={submitData}
        itemsGroup={itemsGroup}
      />
      <AddScoreGroup
        key={addTypeModalVisible ? 'show' : 'hidden'}
        open={addTypeModalVisible}
        onCancel={() => setAddTypeModalVisible(false)}
        onSave={onAddGroupType}
      />
    </div>
  );
}
